import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { redirectToLogin as redirectToLoginWithAlias, protegerSessao } from '../services/httpClient';

interface MenuItem {
  id: string;
  label: string;
  href?: string;
  icon?: string;
  children?: MenuItem[];
}

interface UserSession {
  email: string;
  alias: string;
  clinicName: string;
  role: string;
  userId: string;
  entityId: string;
  entityName: string;
  profileId: string;
  profileName: string;
  permissions: string[];
  allowedPages: string[]; // páginas (internal_page) que o profile pode acessar — usado no route guard
  menuItems: MenuItem[]; // itens visíveis no menu (show_in_menu && access_logged_in && sem internal_tab)
  loginTime: string;
  loginTimestamp: number;
  sessionDuration: number;
  clinic?: string;
  mustChangePassword?: boolean; // conta com senha padrão: força troca antes de usar o sistema
}

interface AuthContextType {
  user: UserSession | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** RN-006: tela bloqueada por inatividade — só a senha (renewSession) destrava */
  isLocked: boolean;
  login: (email: string, password: string, rememberMe: boolean, clinic: string) => Promise<{ success: boolean; error?: string; mustChangePassword?: boolean }>;
  logout: (redirectToLogin?: boolean) => void;
  markPasswordChanged: (newToken: string) => void;
  renewSession: (password: string) => Promise<{ success: boolean; error?: string }>;
  updateProfile: (entityId: string, profileId: string) => void;
  getTimeRemaining: () => number;
  refreshSession: () => void;
  refreshPermissions: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5110/api';

// RN-006 — sessão e inatividade (decisão do Hilton, 29/08/2026). Três relógios:
// bloqueio de tela após IDLE_LOCK_MINUTES sem atividade (só a senha destrava, nada se perde);
// logout real após IDLE_LOGOUT_MINUTES sem atividade; teto de SESSION_MAX_SECONDS desde a última
// senha digitada mesmo com uso contínuo. O token JWT (curto) é renovado em silêncio enquanto há uso.
export const IDLE_LOCK_MINUTES = 15;
export const IDLE_LOGOUT_MINUTES = 120;
export const SESSION_MAX_SECONDS = 12 * 3600;
const ACTIVITY_KEY = 'clinic4us-last-activity';
const TOKEN_REFRESH_AHEAD_MS = 20 * 60 * 1000;

// Instante de expiração do JWT (claim exp, em segundos) em milissegundos; null se ilegível.
const decodeJwtExp = (token: string): number | null => {
  try {
    const payload = token.split('.')[1];
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(normalized));
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
};

// Cabeçalho de autenticação (JWT) para as chamadas feitas via fetch neste contexto.
const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem('clinic4us-token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Extrai o id do usuário (claim `sub`) do JWT, sem validar a assinatura (validação é no backend).
// O backend hoje emite `sub` como "UserId { Value = <guid> }" (ToString do tipo forte), então
// extraímos o GUID por regex — funciona também caso o backend passe a emitir o GUID puro.
const decodeJwtSub = (token: string): string | null => {
  try {
    const payload = token.split('.')[1];
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(normalized));
    const raw = String(json.sub || json.nameid || json.id || '');
    const match = raw.match(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/);
    return match ? match[0] : (raw || null);
  } catch {
    return null;
  }
};

// Lê o claim must_change_password do JWT (conta com senha padrão que precisa trocar no 1º acesso).
const decodeMustChangePassword = (token: string): boolean => {
  try {
    const payload = token.split('.')[1];
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(normalized));
    return String(json.must_change_password) === 'true';
  } catch {
    return false;
  }
};

/**
 * Falhas de login cuja mensagem do servidor PODE ser mostrada ao usuário.
 *
 * A recusa por credencial continua genérica de propósito: dizer qual dos dois campos errou
 * entrega meio caminho a quem está tentando adivinhar. Mas estes três não falam de senha —
 * falam do estado da conta ou do sistema — e esconder o motivo só gera telefonema:
 *
 * - AccessRestricted: a senha está certa, o sistema é que ainda não abriu (RN-012). Sem esta
 *   mensagem o usuário conclui que a senha recém-criada não funciona.
 * - LockedOut: quantos minutos faltam. Sem ela a pessoa segue tentando e renova o bloqueio.
 * - NoActiveEntity: conta sem vínculo ativo, que só o administrador resolve.
 */
const LOGIN_ERRORS_SAFE_TO_SHOW = ['Users.AccessRestricted', 'User.LockedOut', 'Users.NoActiveEntity'];

const loginErrorMessage = async (response: Response): Promise<string> => {
  const generic = 'Credenciais inválidas. Tente novamente.';
  try {
    const body = await response.json();
    return LOGIN_ERRORS_SAFE_TO_SHOW.includes(body?.code) && body?.description
      ? body.description
      : generic;
  } catch {
    return generic;
  }
};

// Lê a última sessão do usuário (entity/profile) na tabela current_session_user.
const fetchCurrentSession = async (
  userId: string
): Promise<{ entityId: string; entityName: string; profileId: string; profileName: string } | null> => {
  try {
    const r = await fetch(`${apiBase}/CurrentSessionUsers/by-user/${userId}`, { headers: { ...authHeader() } });
    if (!r.ok) return null;
    const s = await r.json();
    return {
      entityId: s.entity || '',
      entityName: s.entityName || '',
      profileId: s.profile || '',
      profileName: s.profileName || '',
    };
  } catch {
    return null;
  }
};

// Monta páginas permitidas + menu visível a partir das functionalities atreladas ao profile.
const fetchProfileMenu = async (profileId: string): Promise<{ allowedPages: string[]; menuItems: MenuItem[] }> => {
  try {
    const r = await fetch(`${apiBase}/Functionalities/by-profile/${profileId}`, { headers: { ...authHeader() } });
    if (!r.ok) return { allowedPages: [], menuItems: [] };
    let items: Array<{
      functionalityId: string;
      functionalityTitle?: string;
      internalPage?: string | null;
      internalTab?: string | null;
      order?: number | null;
      showInMenu: boolean;
      accessLoggedIn: boolean;
      categoryTitle?: string | null;
    }> = await r.json();

    // Assistente do prontuário (item 206): o grant é por perfil, mas o uso exige o agente do assistente
    // liberado para a pessoa. Quem não pode usar não vê o item nem abre a página pela URL.
    if (items.some((i) => i.internalPage === 'patient-assistant')) {
      let available = false;
      try {
        const a = await fetch(`${apiBase}/patient-assistant/availability`, { headers: { ...authHeader() } });
        available = a.ok && (await a.json()).available === true;
      } catch {
        available = false;
      }
      if (!available) items = items.filter((i) => i.internalPage !== 'patient-assistant');
    }

    // Acesso: toda functionality ativa do profile que aponta para uma página real.
    const allowedPages = Array.from(
      new Set(items.filter((i) => i.internalPage).map((i) => i.internalPage as string))
    );

    // Menu visível: show_in_menu && access_logged_in && sem internal_tab (abas não entram no menu).
    // Itens com categoryTitle são agrupados num submenu com esse rótulo (ex.: Relatórios).
    // Dentro do grupo vale o `order` da central, empate em ordem alfabética (pedido do
    // Hilton, 15/08/2026 — substitui o alfabético puro de 17/07, que jogava os "+" na frente);
    // sem `order` vai para o fim do grupo.
    // Quem tem a página Assistentes entra no Prontuário por ela (D-OP-16): o item antigo sai do menu, a
    // rota continua valendo para links salvos. Sem o grant de Assistentes, o item antigo continua.
    const hasAssistants = allowedPages.includes('assistants');
    const visible = items
      .filter((i) => i.showInMenu && i.accessLoggedIn && !i.internalTab && i.internalPage)
      .filter((i) => !(hasAssistants && i.internalPage === 'patient-assistant'))
      .sort((a, b) => {
        const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
        const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
        return orderA !== orderB
          ? orderA - orderB
          : (a.functionalityTitle || '').localeCompare(b.functionalityTitle || '', 'pt-BR');
      });
    const menuItems: MenuItem[] = [];
    const groups = new Map<string, MenuItem>();
    for (const i of visible) {
      const item: MenuItem = {
        id: i.functionalityId,
        label: i.functionalityTitle || '',
        href: `?page=${i.internalPage}`,
      };
      if (i.categoryTitle) {
        let group = groups.get(i.categoryTitle);
        if (!group) {
          group = { id: `group-${i.categoryTitle}`, label: i.categoryTitle, children: [] };
          groups.set(i.categoryTitle, group);
          menuItems.push(group);
        }
        group.children!.push(item);
      } else {
        menuItems.push(item);
      }
    }

    // Topo do menu fixo (pedidos do Hilton, 08/08, 15/08 e 22/08/2026): Dashboard é a porta de
    // entrada; Agenda e Oportunidades são operação diária e vêm soltas logo abaixo (fora do
    // grupo Pacientes); o Pipeline do CRM vem logo depois, por ser a mesma natureza de
    // trabalho; Pacientes na sequência e o agregador CRM (configuração e backlog) após.
    // Do sétimo em diante, ordem alfabética — assim o menu não muda
    // de lugar quando alguém mexe no `order` de uma funcionalidade na central.
    const rank = (item: MenuItem): number => {
      if (item.href === '?page=dashboard') return 0;
      if (item.href === '?page=schedule') return 1;
      // Bloqueios de agenda (RN-118n) logo abaixo de Agenda (pedido do Hilton, 30/09/2026)
      if (item.href === '?page=schedule-blocks') return 1.5;
      // Assistentes (item 228) no lugar onde ficava o Assistente do prontuário, logo abaixo de Bloqueios de agenda
      if (item.href === '?page=assistants' || item.href === '?page=patient-assistant') return 1.6;
      // Agenda do dia (RN-546) fica colada abaixo de Agenda (pedido do Hilton, 06/09/2026)
      if (item.href === '?page=day-schedule') return 2;
      // Lista de pacientes saiu do grupo e subiu para cá (RN-004k, 07/09/2026): é tela de uso
      // diário como Dashboard e Agenda, e dois cliques para chegar nela eram atrito puro.
      // ATENÇÃO: esta ordem é do CÓDIGO, não do `order` da central — mexer no banco não move
      // item de primeiro nível, só a posição dentro de um submenu.
      if (item.href === '?page=patients') return 3;
      // Mapa de salas (RN-549) logo abaixo
      if (item.href === '?page=room-map') return 4;
      if (item.href === '?page=opportunities') return 5;
      // Pipeline sai do agregador e senta ao lado de Oportunidades: as duas respondem
      // "quem eu preciso contatar hoje?" (decisão do Hilton, 23/08/2026).
      if (item.href === '?page=crm-funnel') return 6;
      if (item.label === 'Pacientes') return 7;
      if (item.label === 'CRM') return 8;
      return 9;
    };
    menuItems.sort((a, b) => {
      const byRank = rank(a) - rank(b);
      return byRank !== 0 ? byRank : a.label.localeCompare(b.label, 'pt-BR');
    });

    return { allowedPages, menuItems };
  } catch {
    return { allowedPages: [], menuItems: [] };
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLocked, setIsLocked] = useState(false);
  const lockedRef = React.useRef(false);
  lockedRef.current = isLocked;

  // Com a tela bloqueada, um 401 em voo não pode apagar a sessão: o usuário está justamente
  // digitando a senha para renovar (RN-006e).
  useEffect(() => {
    protegerSessao(isLocked);
    return () => protegerSessao(false);
  }, [isLocked]);

  // Carregar sessão do localStorage ao inicializar
  useEffect(() => {
    const loadSession = () => {
      try {
        const sessionData = localStorage.getItem('clinic4us-user-session');
        if (sessionData) {
          const session = JSON.parse(sessionData);

          const loginTimestamp = session.loginTimestamp || new Date(session.loginTime).getTime();
          const sessionDuration = session.sessionDuration || 7200;
          const currentTime = Date.now();
          const elapsedSeconds = Math.floor((currentTime - loginTimestamp) / 1000);

          // Descarta sessões no formato antigo (sem allowedPages) — força novo login para
          // que o guard tenha os dados corretos, evitando bloqueio indevido de todas as páginas.
          if (elapsedSeconds < sessionDuration && Array.isArray(session.allowedPages)) {
            setUser(session);
          } else {
            localStorage.removeItem('clinic4us-user-session');
            localStorage.removeItem('clinic4us-remember-me');
          }
        }
      } catch (error) {
        console.error('Erro ao carregar sessão:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSession();
  }, []);

  // Função de login
  const login = async (
    email: string,
    password: string,
    rememberMe: boolean,
    clinic: string
  ): Promise<{ success: boolean; error?: string; mustChangePassword?: boolean }> => {
    try {
      // Autentica via API contra a tabela Users do banco do tenant (alias). A senha nunca é validada no front.
      const response = await fetch(`${apiBase}/users/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, alias: clinic }),
      });

      if (!response.ok) {
        return { success: false, error: await loginErrorMessage(response) };
      }

      // O endpoint retorna o token JWT como texto puro (text/plain).
      const token = await response.text();

      // Guarda o token já aqui para que as chamadas do bootstrap (by-user/by-profile) o enviem.
      localStorage.setItem('clinic4us-token', token);

      // Bootstrap de sessão: id do usuário (JWT) -> entity/profile (current_session_user) -> menu (functionality_profiles).
      const userId = decodeJwtSub(token) || '';
      const mustChangePassword = decodeMustChangePassword(token);
      let entityId = '';
      let entityName = '';
      let profileId = '';
      let profileName = '';
      let allowedPages: string[] = [];
      let menuItems: MenuItem[] = [];

      if (userId) {
        const session = await fetchCurrentSession(userId);
        if (session) {
          entityId = session.entityId;
          entityName = session.entityName;
          profileId = session.profileId;
          profileName = session.profileName;
          if (profileId) {
            const menu = await fetchProfileMenu(profileId);
            allowedPages = menu.allowedPages;
            menuItems = menu.menuItems;
          }
        }
      }

      const loginTimestamp = Date.now();
      const userSession: UserSession = {
        email,
        alias: clinic,
        clinicName: entityName || clinic || 'Clinic4Us',
        role: profileName,
        userId,
        entityId,
        entityName,
        profileId,
        profileName,
        permissions: [],
        allowedPages,
        menuItems,
        loginTime: new Date().toISOString(),
        loginTimestamp,
        sessionDuration: SESSION_MAX_SECONDS,
        clinic,
        mustChangePassword,
      };

      localStorage.setItem('clinic4us-user-session', JSON.stringify(userSession));
      localStorage.setItem('clinic4us-token', token);

      if (rememberMe) {
        localStorage.setItem('clinic4us-remember-me', 'true');
      }

      setUser(userSession);

      return { success: true, mustChangePassword };
    } catch (error) {
      console.error('Erro ao fazer login:', error);
      return { success: false, error: 'Erro ao processar login. Tente novamente.' };
    }
  };

  // Função de logout
  const logout = (redirect: boolean = true) => {
    try {
      localStorage.removeItem('clinic4us-token');
      localStorage.removeItem('clinic4us-user-session');
      localStorage.removeItem('clinic4us-remember-me');
      setUser(null);

      if (redirect) {
        // Volta para o login mantendo o alias no path (ex.: /Ninho).
        redirectToLoginWithAlias();
      }
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  // Função para renovar sessão (revalida senha; mantém entity/profile/menu).
  const renewSession = async (password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      if (!user) {
        return { success: false, error: 'Sessão não encontrada.' };
      }

      const response = await fetch(`${apiBase}/users/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email, password, alias: user.clinic || user.alias }),
      });
      if (!response.ok) {
        return { success: false, error: 'Senha incorreta.' };
      }

      // O token NOVO precisa ser guardado — sem isto o antigo expirava e a próxima chamada
      // derrubava a sessão logo depois de "renovar" (defeito corrigido em 29/08/2026).
      const token = await response.text();
      localStorage.setItem('clinic4us-token', token);

      // Senha digitada = nova âncora do teto de 12 h e da inatividade
      const newTimestamp = Date.now();
      const updatedSession: UserSession = {
        ...user,
        loginTime: new Date().toISOString(),
        loginTimestamp: newTimestamp,
        sessionDuration: SESSION_MAX_SECONDS,
      };

      localStorage.setItem('clinic4us-user-session', JSON.stringify(updatedSession));
      localStorage.setItem(ACTIVITY_KEY, String(newTimestamp));
      setUser(updatedSession);
      setIsLocked(false);

      return { success: true };
    } catch (error) {
      console.error('Erro ao renovar sessão:', error);
      return { success: false, error: 'Erro ao renovar sessão. Tente novamente.' };
    }
  };

  // Troca de entidade/perfil: re-busca menu e acessos do novo profile.
  // Troca de entidade/perfil (botão "Mudar Perfil"): persiste em current_session_user (PUT by-user),
  // re-busca nomes/fuso (by-user) e menu/acessos (by-profile), atualiza a sessão e vai para o dashboard.
  const updateProfile = (entityId: string, profileId: string) => {
    if (!user) return;

    (async () => {
      try {
        await fetch(`${apiBase}/CurrentSessionUsers/by-user/${user.userId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...authHeader() },
          body: JSON.stringify({ entity: entityId, profile: profileId }),
        });

        const session = await fetchCurrentSession(user.userId);
        const menu = await fetchProfileMenu(profileId);
        const newTimestamp = Date.now();
        const updatedSession: UserSession = {
          ...user,
          entityId,
          entityName: session?.entityName || '',
          profileId,
          profileName: session?.profileName || '',
          role: session?.profileName || '',
          clinicName: session?.entityName || user.clinicName,
          allowedPages: menu.allowedPages,
          menuItems: menu.menuItems,
          loginTime: new Date().toISOString(),
          loginTimestamp: newTimestamp,
          sessionDuration: SESSION_MAX_SECONDS,
        };

        localStorage.setItem('clinic4us-user-session', JSON.stringify(updatedSession));
        setUser(updatedSession);

        // Recarrega numa página segura — a página atual pode não estar liberada no novo perfil.
        window.location.href = `${window.location.origin}/?page=dashboard`;
      } catch (error) {
        console.error('Erro ao trocar perfil:', error);
      }
    })();
  };

  // Tempo restante da sessão (segundos)
  const getTimeRemaining = (): number => {
    if (!user) return 0;

    try {
      const currentTime = Date.now();
      const elapsedSeconds = Math.floor((currentTime - user.loginTimestamp) / 1000);
      return Math.max(0, user.sessionDuration - elapsedSeconds);
    } catch (error) {
      console.error('Erro ao calcular tempo restante:', error);
      return 0;
    }
  };

  // Forçar releitura da sessão do localStorage
  const refreshSession = () => {
    try {
      const sessionData = localStorage.getItem('clinic4us-user-session');
      if (sessionData) {
        const session = JSON.parse(sessionData);
        setUser(session);
      }
    } catch (error) {
      console.error('Erro ao atualizar sessão:', error);
    }
  };

  // Limpa a flag de troca obrigatória após o usuário trocar a senha (libera o acesso ao sistema).
  // O token anterior morre com a senha antiga (revogação por troca de senha), então a sessão
  // segue com o token reemitido pelo endpoint da troca.
  const markPasswordChanged = (newToken: string) => {
    // Persistência síncrona: a tela de troca navega para o dashboard logo depois desta
    // chamada, e um updater do setUser pode não rodar antes da saída da página — a sessão
    // gravada ficaria com a pendência de troca e o usuário voltaria para a mesma tela.
    localStorage.setItem('clinic4us-token', newToken);
    if (user) {
      localStorage.setItem(
        'clinic4us-user-session',
        JSON.stringify({ ...user, mustChangePassword: false })
      );
    }
    setUser((prev) => (prev ? { ...prev, mustChangePassword: false } : prev));
  };

  const isAuthenticated = !!user && getTimeRemaining() > 0;

  // Revalida menu/acessos do profile atual contra a API (não sobrescreve em caso de falha).
  const refreshPermissions = async (): Promise<void> => {
    try {
      if (!user || !user.profileId) {
        return;
      }

      const menu = await fetchProfileMenu(user.profileId);
      if (menu.allowedPages.length === 0 && menu.menuItems.length === 0) {
        return; // falha de rede — manter o que já está em sessão
      }

      const updatedSession: UserSession = {
        ...user,
        allowedPages: menu.allowedPages,
        menuItems: menu.menuItems,
      };

      localStorage.setItem('clinic4us-user-session', JSON.stringify(updatedSession));
      setUser(updatedSession);
    } catch (error) {
      console.error('Erro ao revalidar permissões:', error);
    }
  };

  // Revalidar permissões periodicamente (a cada 5 minutos) enquanto logado
  useEffect(() => {
    if (!user || !isAuthenticated) {
      return;
    }

    refreshPermissions();

    const intervalId = setInterval(() => {
      refreshPermissions();
    }, 300000);

    return () => {
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, isAuthenticated]);

  // ---- RN-006: atividade, bloqueio e logout por inatividade ----
  // O carimbo de atividade vive no localStorage: mexer em qualquer aba conta para todas, e
  // todas bloqueiam/destravam juntas. Com a tela bloqueada, mouse e teclado NÃO contam — só a
  // senha (renewSession) destrava. O login também conta como atividade (loginTimestamp).
  useEffect(() => {
    if (!user || !isAuthenticated) {
      setIsLocked(false);
      return;
    }

    let lastWrite = 0;
    const onActivity = () => {
      if (lockedRef.current) return;
      const now = Date.now();
      if (now - lastWrite < 5000) return;
      lastWrite = now;
      localStorage.setItem(ACTIVITY_KEY, String(now));
    };
    const events: Array<keyof WindowEventMap> = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((name) => window.addEventListener(name, onActivity, { passive: true }));

    const tick = () => {
      const stored = Number(localStorage.getItem(ACTIVITY_KEY) || 0);
      const lastActivity = Math.max(stored, user.loginTimestamp || 0);
      const idleMinutes = (Date.now() - lastActivity) / 60000;
      if (idleMinutes >= IDLE_LOGOUT_MINUTES) {
        logout(true);
        return;
      }
      setIsLocked(idleMinutes >= IDLE_LOCK_MINUTES);
    };
    tick();
    const intervalId = setInterval(tick, 5000);

    // Sincronia entre abas (RN-002b). O evento `storage` só dispara nas OUTRAS abas da mesma
    // origem, que é exatamente quem precisa reagir.
    //
    // Por que isto importa: o perfil ativo é UM POR USUÁRIO no servidor (`current_session_user`),
    // e a autorização é lida de lá a cada requisição. Sem esta sincronia, trocar de perfil numa
    // aba mudava o servidor para todas, enquanto as outras seguiam exibindo o menu do perfil
    // antigo — a pessoa via uma coisa e o servidor respondia outra.
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'clinic4us-token' && e.newValue === null) {
        logout(true);
        return;
      }

      if (e.key !== 'clinic4us-user-session' || !e.newValue) {
        return;
      }

      try {
        const nova: UserSession = JSON.parse(e.newValue);

        // Outra PESSOA entrou neste navegador: o token no storage já é dela, então esta aba não
        // pode seguir com a identidade antiga — nada do que ela mostra vale mais.
        if (nova.userId !== user.userId) {
          window.location.reload();
          return;
        }

        // Mesmo usuário trocou de perfil ou de entidade: adota o que passou a valer no servidor.
        if (nova.profileId !== user.profileId || nova.entityId !== user.entityId) {
          setUser(nova);

          // A página aberta pode não existir no perfil novo. Sair dela é obrigatório: ficar
          // renderizando uma tela que o servidor vai recusar só gera erro sem explicação.
          const paginaAtual = new URLSearchParams(window.location.search).get('page');
          const liberada = !paginaAtual || (nova.allowedPages || []).includes(paginaAtual);
          if (!liberada) {
            window.location.href = `${window.location.origin}/?page=dashboard`;
          }
        }
      } catch {
        // Sessão ilegível no storage: não é motivo para derrubar a aba
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      events.forEach((name) => window.removeEventListener(name, onActivity));
      clearInterval(intervalId);
      window.removeEventListener('storage', onStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId, user?.profileId, user?.entityId, user?.loginTimestamp, isAuthenticated]);

  // Token curto (60 min no servidor) renovado em silêncio enquanto há uso e a tela não está
  // bloqueada — a inatividade é tratada pelo bloqueio, o teto de 12 h pela sessão.
  useEffect(() => {
    if (!user || !isAuthenticated) {
      return;
    }

    // Renova TAMBEM com a tela bloqueada (08/09/2026): o bloqueio protege a tela, nao encerra a
    // sessao — quem esta bloqueado segue autenticado. Com a guarda antiga, 15 min parado travavam
    // a renovacao e o token morria sozinho aos 60 min, jogando o usuario no login. A inatividade
    // continua tratada pelos outros dois relogios: logout aos IDLE_LOGOUT_MINUTES e teto de 12 h.
    const refreshIfNeeded = async () => {
      const token = localStorage.getItem('clinic4us-token');
      const exp = token ? decodeJwtExp(token) : null;
      if (!exp || exp - Date.now() > TOKEN_REFRESH_AHEAD_MS) return;
      try {
        const r = await fetch(`${apiBase}/users/refresh`, { method: 'POST', headers: { ...authHeader() } });
        if (r.ok) {
          localStorage.setItem('clinic4us-token', await r.text());
        }
      } catch {
        // sem rede agora: a próxima chamada da API decide (401 força o login)
      }
    };
    refreshIfNeeded();
    const intervalId = setInterval(refreshIfNeeded, 60000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId, isAuthenticated]);

  const contextValue: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    isLocked,
    login,
    logout,
    markPasswordChanged,
    renewSession,
    updateProfile,
    getTimeRemaining,
    refreshSession,
    refreshPermissions,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook para usar o contexto de autenticação
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
